<template lang="pug">
  v-card.overflow-hidden
    v-card-title
      h2.card-title {{ 'base.report' | translate }}
    table.table
      thead
        tr.table__row.table__row--head
          th.table__cell {{ 'base.ingredients' | translate }}
          th.table__cell.text-right
            span.table__label {{ 'base.weight' | translate }}
            span.table__unit [{{ 'base.kg' | translate }}]
            br
            span.table__sub-label {{ 'base.plan_fact' | translate }}
          th.table__cell.text-right
            span.table__label {{ 'base.deviation' | translate }}
            span.table__unit [% / {{ 'base.kg' | translate }}]
          th.table__cell.text-right {{ 'base.loading_start' | translate }}
          th.table__cell.text-right {{ 'base.loading_time' | translate }}
          th.table__cell.text-right {{ 'base.operator' | translate }}
      tbody
        tr.table__row(
          v-for="item in ingredients"
          :key="item.id")
          td.table__cell.nowrap {{ item.name }}
          td.table__cell.nowrap.text-right
            span(:class="allowViewMonitoringIngredient ? getIngredientColorClass(item) : ''") {{ formateWeightLabel(item) }}
          td.table__cell.nowrap.text-right {{ formatePercentDiffLabel(item) }}
          td.table__cell.nowrap.text-right {{ formateTimeString(item.loaded_at) }}
          td.table__cell.nowrap.text-right {{ formateTimeLoadString(item) }}
          td.table__cell.nowrap.text-right {{ formateWorkerLabel(item) }}
        tr.table__row
          td.table__cell.bg-gray.nowrap {{ 'base.total' | translate }}:
          td.table__cell.bg-gray.nowrap.text-right
            span(:class="allowViewMonitoringFarm ? getTotalColorClass() : ''") {{ formateWeightLabel(total) }}
          td.table__cell.bg-gray.nowrap.text-right {{ total.diff_weight }} {{ 'base.kg' | translate }}
          td.table__cell.bg-gray
          td.table__cell.bg-gray.nowrap.text-right {{ formateTimeLoadString(total) }}
          td.table__cell.bg-gray
</template>

<script>
import { timeString } from '@/util'
import api from '@/api'

export default {
  name: 'MixerLoadsDetail',

  props: {
    selected: Object,
    ingreds: Array,
  },

  async mounted () {
    this.calculateTotalValues()

    if (this.allowViewMonitoringIngredient || this.allowViewMonitoringFarm) {
      await this.loadMonitoringSettings()

      if (this.allowViewMonitoringIngredient) {
        await this.loadIngredientsSettings()
      }

      this.$forceUpdate()
    }
  },

  data: () => ({
    total: {
      plan_weight: 0,
      fact_weight: 0,
      diff_weight: 0,
      time_load: 0
    },
    monitoringSettings: null,
    ingredientsSettings: {}
  }),

  computed: {
    farmId () {
      return this.$route.params.farm_id
    },

    ingredients () {
      if (this.selected) {
        return this.selected.schedule_ration.ingredients
      }
      if (this.ingreds) {
        return this.ingreds
      }
      return []
    },

    allowViewMonitoringIngredient () {
      return this.hasPermission('monitoring_ingredient', 'view')
    },

    allowViewMonitoringFarm () {
      return this.hasPermission('monitoring_farm', 'view')
    }
  },

  methods: {
    async loadMonitoringSettings () {
      if (!this.farmId) {
        return
      }

      try {
        const response = await api.monitoring.farms(this.farmId)
        this.monitoringSettings = response.data
      } catch (error) {
        console.error('Error loading monitoring settings:', error)
      }
    },

    async loadIngredientsSettings () {
      if (!this.farmId) {
        return
      }

      try {
        const response = await api.monitoring.ingredients({ farm: this.farmId })

        if (response.data && Array.isArray(response.data)) {
          const newSettings = {}

          response.data.forEach(setting => {
            if (setting.ingredient && setting.ingredient.id) {
              newSettings[setting.ingredient.id] = setting
            }
          })

          this.ingredientsSettings = newSettings
        }
      } catch (error) {
        console.error('Error loading ingredients:', error)
      }
    },

    getIngredientColorClass (item) {
      if (!this.allowViewMonitoringFarm) {
        return ''
      }

      const ingredientSettings = this.ingredientsSettings[item.ingredient]

      if (!ingredientSettings || ingredientSettings.level === null) {
        return ''
      }

      // Skip only if fact_weight is null (no data recorded)
      // If fact_weight is 0, we should still calculate deviation (100% error)
      if (item.fact_weight === null) {
        return ''
      }

      const deviationPercent = this.calculateDeviationPercent(item)
      const absDeviation = Math.abs(deviationPercent)

      let threshold = 0

      if (ingredientSettings.level === 'percent') {
        threshold = ingredientSettings.percent
      } else if (ingredientSettings.level === 'weight') {
        if (item.plan_weight && item.plan_weight > 0) {
          threshold = (ingredientSettings.weight / item.plan_weight) * 100
        }
      }

      if (threshold === 0 || threshold === null) {
        return ''
      }

      if (absDeviation > threshold) {
        return 'ingredient-red'
      }

      return ''
    },

    getTotalColorClass () {
      if (!this.allowViewMonitoringFarm) {
        return ''
      }

      if (!this.monitoringSettings || !this.monitoringSettings.load_weight) {
        return ''
      }

      const settings = this.monitoringSettings.load_weight

      if (!settings || !settings.enabled) {
        return ''
      }

      const planWeight = this.total.plan_weight
      const factWeight = this.total.fact_weight

      // Skip if no plan weight or if fact weight is not recorded (null/undefined)
      // But if factWeight is 0, we should still calculate deviation (100% error)
      if (!planWeight || planWeight === 0) {
        return ''
      }

      if (factWeight === null || factWeight === undefined) {
        return ''
      }

      const deviationPercent = ((factWeight - planWeight) / planWeight) * 100
      const absDeviation = Math.abs(deviationPercent)

      let threshold = null

      if (settings.level === 'percent') {
        threshold = settings.percent
      } else if (settings.level === 'weight' && planWeight > 0) {
        threshold = (settings.weight / planWeight) * 100
      }

      if (threshold !== null && threshold !== undefined && threshold !== 0) {
        if (absDeviation <= threshold) {
          return 'weight-green'
        }
        return 'weight-red'
      }

      if (settings.green !== 0 && absDeviation <= settings.green) {
        return 'weight-green'
      }

      if (settings.red !== 0 && absDeviation >= settings.red) {
        return 'weight-red'
      }

      if (settings.yellow !== 0 && absDeviation < settings.yellow) {
        return 'weight-yellow'
      }

      return ''
    },

    calculateDeviationPercent (item) {
      if (!item.plan_weight || item.plan_weight === 0) {
        return 0
      }

      const deviation = ((item.fact_weight - item.plan_weight) / item.plan_weight) * 100
      return deviation
    },

    formateTimeString (str) {
      return timeString(str)
    },

    formateWeightLabel (item) {
      let plan = item.plan_weight !== null ? item.plan_weight.toFixed(0) : '-'
      let fact = item.fact_weight !== null ? item.fact_weight.toFixed(0) : '-'
      return plan + ' / ' + fact
    },

    formatePercentDiffLabel (item) {
      let percent = item.diff_in_percent !== null ? Math.round(item.diff_in_percent) + '%' : '-'
      let weight = item.fact_weight !== null ? Math.round(item.fact_weight - item.plan_weight) : '-'
      return percent + ' / ' + weight
    },

    formateTimeLoadString (item) {
      return item.time_load ? Math.round(item.time_load / 60) + ' ' + this.$t('base.min') : '-'
    },

    formateWorkerLabel (item) {
      return item.worker !== null ? item.worker : '-'
    },

    calculateTotalValues () {
      for (let item of this.ingredients) {
        this.total.plan_weight += item.plan_weight ? Math.round(item.plan_weight) : 0
        this.total.fact_weight += item.fact_weight ? Math.round(item.fact_weight) : 0
        this.total.diff_weight += item.plan_weight && item.fact_weight ? Math.round(item.fact_weight - item.plan_weight) : 0
        this.total.time_load += item.time_load || 0
      }
    }
  }
}
</script>

<style scoped>
.ingredient-red {
  background-color: #FF9191 !important;
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  display: inline-block;
}

.weight-green {
  background-color: #91EA91;
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  display: inline-block;
}

.weight-yellow {
  background-color: #FBE268;
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  display: inline-block;
}

.weight-red {
  background-color: #FF9191;
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  display: inline-block;
}
</style>